import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { validateSubCategory } from "../../../utils/validation";
import {
  fetchSubCategoryData,
  addSubCategory,
  updateSubCategory,
} from "../../../services/MasterSettings/subCategoryServices";
import { getAllCategories } from "../../../services/MasterSettings/categoryServices";
import allImages from "../../../assets/images-import";
import { handleErrors } from "../../../utils/errorHandler";
import { Loading } from "../../Common/OtherElements/Loading";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { resolveFileUrl } from "../../../utils/fileUrlHelper";

const INITIAL_FORM_STATE = {
  CategoryId: "",
  SubCategoryName: "",
  SubCategorySlug: "",
  ShortDesc: "",
  FullDesc: "",
  MetaTitle: "",
  MetaDesc: "",
  MetaKeywords: "",
  SubCategoryImage: "",
  SubCategoryImagePreview: "",
};

export const AddSubCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [content, setContent] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [categories, setCategories] = useState([]);
  const [PageLevelAccessurl, setPageLevelAccessurl] = useState();

  const generateSubCategorySlug = (value) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/\s+/g, "-");

  useEffect(() => {
    if (id) {
      setPageLevelAccessurl("/subcategory/update/:id");
    } else {
      setPageLevelAccessurl("subcategory/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else {
        if (!pageAccessData.addAccess) {
          navigate("/404-error-page");
        }
      }
    } else {
      console.log("No page access details found");
    }
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getAllCategories();
        setCategories(response.data.result || []);
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await fetchSubCategoryData(id);
          if (data) {
            setFormData({
              CategoryId: data.categoryId || "",
              SubCategoryName: data.subCategoryName || "",
              SubCategorySlug: data.subCategorySlug || "",
              ShortDesc: data.shortDesc || "",
              FullDesc: data.fullDesc || "",
              MetaTitle: data.metaTitle || "",
              MetaDesc: data.metaDesc || "",
              MetaKeywords: data.metaKeywords || "",
              SubCategoryImage: "",
              SubCategoryImagePreview: resolveFileUrl(data.subCategoryImage) || "",
            });
            setContent(data.fullDesc || "");
            setIsSlugManuallyEdited(true);
          }
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setContent("");
        setIsSlugManuallyEdited(false);
      }
    };
    fetchData();
  }, [id]);

  const handleEditorChange = (value) => {
    setContent(value);
    setFormData((prevData) => ({ ...prevData, FullDesc: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "SubCategoryName") {
      setFormData((prevData) => ({
        ...prevData,
        SubCategoryName: value,
        SubCategorySlug: isSlugManuallyEdited
          ? prevData.SubCategorySlug
          : generateSubCategorySlug(value),
      }));
    } else if (name === "SubCategorySlug") {
      setIsSlugManuallyEdited(true);
      setFormData((prevData) => ({
        ...prevData,
        SubCategorySlug: generateSubCategorySlug(value),
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        SubCategoryImage: file,
        SubCategoryImagePreview: URL.createObjectURL(file),
      }));
      setErrors((prevErrors) => ({ ...prevErrors, SubCategoryImage: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { valid, errors: validationErrors } = validateSubCategory(formData);
    setErrors(validationErrors);

    if (valid) {
      setLoading(true);
      setIsButtonDisabled(true);
      try {
        if (id) {
          await updateSubCategory({ ...formData, id });
          toast.success("Sub Category updated successfully!");
        } else {
          await addSubCategory(formData);
          toast.success("Sub Category added successfully!");
        }
        navigate("/subcategory");
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
        setIsButtonDisabled(false);
      }
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">{id ? "Update Sub Category" : "Add Sub Category"}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/subcategory">Manage Sub Categories</Link>
                </li>
                <li className="breadcrumb-item">
                  {id ? `Update Sub Category-${id}` : "Add Sub Category"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-2">
        <form onSubmit={handleSubmit} method="POST">
          <div className="row">
            <div className="col-lg-8">
              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper p-1">
                  <h5 className="blogs-heading">Sub Category Details</h5>
                </div>
                <div className="row mt-3">
                  <div className="mb-3 col-md-4">
                    <label className="form-label">
                      Category <span className="required-field">*</span>
                    </label>
                    <select
                      name="CategoryId"
                      value={formData.CategoryId}
                      onChange={handleInputChange}
                      className={`form-select ${
                        errors.CategoryId ? "is-invalid" : ""
                      }`}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.categoryName}
                        </option>
                      ))}
                    </select>
                    {errors.CategoryId && (
                      <div className="invalid-feedback">
                        {errors.CategoryId}
                      </div>
                    )}
                  </div>

                  <div className="mb-3 col-md-4">
                    <label className="form-label">
                      Sub Category Name <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="SubCategoryName"
                      value={formData.SubCategoryName}
                      placeholder="Enter Sub Category Name"
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.SubCategoryName ? "is-invalid" : ""
                      }`}
                    />
                    {errors.SubCategoryName && (
                      <div className="invalid-feedback">
                        {errors.SubCategoryName}
                      </div>
                    )}
                  </div>

                  <div className="mb-3 col-md-4">
                    <label className="form-label">
                      Sub Category Slug <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="SubCategorySlug"
                      value={formData.SubCategorySlug}
                      placeholder="Enter Sub Category Slug"
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.SubCategorySlug ? "is-invalid" : ""
                      }`}
                    />
                    {errors.SubCategorySlug && (
                      <div className="invalid-feedback">
                        {errors.SubCategorySlug}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Short Description</label>
                  <textarea
                    name="ShortDesc"
                    value={formData.ShortDesc}
                    placeholder="Enter Short Description"
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.ShortDesc ? "is-invalid" : ""
                    }`}
                    rows="3"
                  ></textarea>
                  {errors.ShortDesc && (
                    <div className="invalid-feedback">{errors.ShortDesc}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Full Description</label>
                  <Editor
                    tinymceScriptSrc="/tinymce/tinymce.min.js"
                    value={content}
                    init={{
                      height: 400,
                      menubar: false,
                      plugins: [
                        "advlist",
                        "autolink",
                        "link",
                        "image",
                        "lists",
                        "charmap",
                        "preview",
                        "anchor",
                        "pagebreak",
                        "searchreplace",
                        "wordcount",
                        "visualblocks",
                        "code",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "emoticons",
                        "template",
                        "help",
                      ],
                      toolbar:
                        "undo redo | styles | bold italic | forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | preview media fullscreen | emoticons | help",
                    }}
                    onEditorChange={handleEditorChange}
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper">
                  <h5 className="">Sub Category Image</h5>
                </div>
                <div className="mt-3 d-flex justify-content-center">
                  <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                    <img
                      src={
                        formData.SubCategoryImagePreview
                          ? resolveFileUrl(formData.SubCategoryImagePreview)
                          : allImages.DefultImage
                      }
                      className="rounded-circle avatar-xl img-thumbnail user-profile-image shadow"
                      alt="Sub Category Preview"
                    />
                    <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                      <input
                        id="subCategoryImage"
                        type="file"
                        accept="image/*"
                        className="profile-img-file-input"
                        onChange={handleImageChange}
                      />
                      <label
                        htmlFor="subCategoryImage"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body shadow">
                          <i className="ri-camera-fill"></i>
                        </span>
                      </label>
                    </div>
                    {errors.SubCategoryImage && (
                      <div className="invalid-feedback d-block">
                        {errors.SubCategoryImage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper">
                  <h5 className="">Meta Data</h5>
                </div>
                <div className="mt-3">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text"
                    name="MetaTitle"
                    value={formData.MetaTitle}
                    placeholder="Enter Meta Title"
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                <div className="mt-2">
                  <label className="form-label">Meta Keywords</label>
                  <input
                    type="text"
                    name="MetaKeywords"
                    value={formData.MetaKeywords}
                    placeholder="Enter Meta Keywords"
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                <div className="mt-2">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    name="MetaDesc"
                    value={formData.MetaDesc}
                    placeholder="Enter Meta Description"
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12">
            <div className="pt-4">
              <button
                type="submit"
                className="btn btn-secondary"
                disabled={isButtonDisabled}
              >
                {isButtonDisabled
                  ? id
                    ? "Updating"
                    : "Saving"
                  : id
                  ? "Update"
                  : "Save"}
              </button>
              <Link to="/subcategory" className="btn btn-danger ms-1">
                Cancel
              </Link>
            </div>
          </div>
          {loading && <Loading />}
        </form>
      </div>
    </>
  );
};